// --- Backend verification action wrappers (session-scoped step-up auth) ------
//
// The password path mirrors the login flow (verifyOwnerPassword only fetches
// 2FA recipient options / a no-2FA session token, same as /OwnerAuth/login
// does at login time). The SMS resend/verify and passkey/recovery paths hit
// dedicated, session-scoped owner-api endpoints instead (identified from the
// current session, not a client-supplied username) - see OwnerAuthController.
// stepUpResend2fa/stepUpVerify2faCode/passkeyStepUpOptions/passkeyStepUpVerify/
// recoveryVerify. Custom action types are used so the shared auth reducer's
// LOGIN_SUCCESS branch isn't triggered — a mid-session step-up must not
// reset loggedIn / auth2faOptions on the store.

// Runs the standard username + password login to fetch the owner's 2FA
// recipient options (or, for a no-2FA owner, a fresh session token).
// Response shape matches the gateway's `/OwnerAuth/login` — either
// { username, auth2faOptions: [{ value, type, hash }, ...] } for owners
// requiring 2FA, or a session-token object for no-2FA owners.
export function verifyOwnerPassword(username, password, tenant) {
  return {
    types: ['app/verification/PASSWORD', 'app/verification/PASSWORD_SUCCESS', 'app/verification/PASSWORD_FAIL'],
    promise: client => client.post(`/OwnerAuth/login?tenant=${tenant}`, {
      data: { username, password },
    }),
  };
}

// Triggers a Vonage SMS to the picked recipient, for the CURRENT session
// owner - unlike the login-time resend2faCode, no username is sent; owner-api
// identifies the owner from the session itself. Returns
// { userId, twoFactorRequestId, twoFactorRequestExpiry, twoFactorRequestGenerated }.
export function requestStepUpSmsCode(tenant, recipientHash, deliveryMethod) {
  const data = {
    recipientHash: recipientHash ?? undefined,
    attemptedDeliveryMethod: deliveryMethod ?? undefined,
  };
  return {
    types: ['app/verification/SMS_REQUEST', 'app/verification/SMS_REQUEST_SUCCESS', 'app/verification/SMS_REQUEST_FAIL'],
    // tenant goes through ApiClient's dedicated option (sets the X-Tenant
    // header) - gateway's stepUpResend2fa reads it from that header, not a
    // query param (same convention as verifyRecoveryCode below).
    promise: client => client.post('/owners/step-up/resend2faCode', { data, tenant }),
  };
}

// Verifies the entered SMS code against the pending Vonage request for the
// CURRENT session owner (no username - see requestStepUpSmsCode above).
// Mints an updated token with sessionVerified: true on success, same as
// passkeyStepUpVerify/verifyRecoveryCode below - the current login is
// preserved, not swapped for a new session.
export function verifyStepUpSmsCode(twoFactorRequestId, code) {
  return {
    types: ['app/verification/SMS_VERIFY', 'app/verification/SMS_VERIFY_SUCCESS', 'app/verification/SMS_VERIFY_FAIL'],
    promise: client => client.post('/owners/step-up/verify2faCode', {
      data: { twoFactorRequestId, code },
    }),
  };
}

// Mints an updated token with sessionVerified: true on a valid, unused code -
// see requestStepUpSmsCode above for why the current login is preserved
// rather than swapped.
export function verifyRecoveryCode(code, tenant) {
  return {
    types: ['app/verification/RECOVERY', 'app/verification/RECOVERY_SUCCESS', 'app/verification/RECOVERY_FAIL'],
    promise: client => client.post('/owners/recovery/verify', { data: { code }, tenant }),
  };
}

// Session-scoped passkey step-up. Distinct from the login-time passkey
// endpoints because these:
//   - restrict allowCredentials to the current session user's credentials
//     (so someone else's passkey on the same device cannot be offered), and
//   - mint an updated token with sessionVerified: true rather than swapping
//     to a brand new session, so the current login is preserved.
export function passkeyStepUpOptions() {
  return {
    types: ['app/verification/PASSKEY_OPTIONS', 'app/verification/PASSKEY_OPTIONS_SUCCESS', 'app/verification/PASSKEY_OPTIONS_FAIL'],
    promise: client => client.post('/owners/passkey/step-up/options'),
  };
}

export function passkeyStepUpVerify(credential) {
  return {
    types: ['app/verification/PASSKEY_VERIFY', 'app/verification/PASSKEY_VERIFY_SUCCESS', 'app/verification/PASSKEY_VERIFY_FAIL'],
    promise: client => client.post('/owners/passkey/step-up/verify', { data: { credential } }),
  };
}
